import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import type { ApiError } from '../api/ApiError';
import { eventKeys } from './queryKeys';
import type {
  ConfirmResult,
  EventDetail,
  EventPhoto,
  ModerateEventBody,
  RsvpResult,
  RsvpStatus,
  SaveEventBody,
  UpvoteResult,
} from './types';
import { unwrap, unwrapEmpty } from './unwrap';

/**
 * Invalidates every events query.
 *
 * A change to one event ripples into the map, the list, the caller's RSVPs and the moderation queue,
 * so invalidating the whole feature is cheaper than reasoning about which views moved.
 */
function useInvalidateEvents(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: eventKeys.all });
  };
}

/** Submits a new event. */
export function useSubmitEvent(): UseMutationResult<EventDetail, ApiError, SaveEventBody> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<EventDetail, ApiError, SaveEventBody>({
    mutationFn: async (body) =>
      unwrap(await client.POST('/api/events', { body }), 'Could not submit that event'),
    onSuccess: async () => invalidate(),
  });
}

/** Updates an event the caller owns. */
export function useUpdateEvent(
  eventId: string,
): UseMutationResult<EventDetail, ApiError, SaveEventBody> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<EventDetail, ApiError, SaveEventBody>({
    mutationFn: async (body) =>
      unwrap(
        await client.PUT('/api/events/{eventId}', { params: { path: { eventId } }, body }),
        'Could not save that event',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** Cancels an event the caller owns. */
export function useCancelEvent(
  eventId: string,
): UseMutationResult<EventDetail, ApiError, string | null> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<EventDetail, ApiError, string | null>({
    mutationFn: async (version) =>
      unwrap(
        await client.POST('/api/events/{eventId}/cancel', {
          params: { path: { eventId } },
          body: { version },
        }),
        'Could not cancel that event',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** Confirms that a listing is still accurate. */
export function useConfirmEvent(
  eventId: string,
): UseMutationResult<ConfirmResult, ApiError, void> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<ConfirmResult, ApiError, void>({
    mutationFn: async () =>
      unwrap(
        await client.POST('/api/events/{eventId}/confirm', { params: { path: { eventId } } }),
        'Could not confirm that event',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** Adds a community upvote. */
export function useUpvoteEvent(eventId: string): UseMutationResult<UpvoteResult, ApiError, void> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<UpvoteResult, ApiError, void>({
    mutationFn: async () =>
      unwrap(
        await client.POST('/api/events/{eventId}/upvote', { params: { path: { eventId } } }),
        'Could not upvote that event',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** Sets the caller's RSVP. */
export function useSetRsvp(eventId: string): UseMutationResult<RsvpResult, ApiError, RsvpStatus> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<RsvpResult, ApiError, RsvpStatus>({
    mutationFn: async (status) =>
      unwrap(
        await client.PUT('/api/events/{eventId}/rsvp', {
          params: { path: { eventId } },
          body: { status },
        }),
        'Could not save your RSVP',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** Removes the caller's RSVP. */
export function useRemoveRsvp(eventId: string): UseMutationResult<RsvpResult, ApiError, void> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<RsvpResult, ApiError, void>({
    mutationFn: async () =>
      unwrap(
        await client.DELETE('/api/events/{eventId}/rsvp', { params: { path: { eventId } } }),
        'Could not remove your RSVP',
      ),
    onSuccess: async () => invalidate(),
  });
}

/** Approves or rejects a listing, applying any moderator edits. */
export function useModerateEvent(
  eventId: string,
): UseMutationResult<EventDetail, ApiError, ModerateEventBody> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<EventDetail, ApiError, ModerateEventBody>({
    mutationFn: async (body) =>
      unwrap(
        await client.POST('/api/events/{eventId}/moderate', {
          params: { path: { eventId } },
          body,
        }),
        'Could not moderate that event',
      ),
    onSuccess: async () => invalidate(),
  });
}

/**
 * Uploads an event photo.
 *
 * Sent as multipart form data with the body serializer disabled, because the default JSON serializer
 * would stringify the `FormData` and the file would never leave the browser.
 */
export function useAddEventPhoto(eventId: string): UseMutationResult<EventPhoto, ApiError, File> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<EventPhoto, ApiError, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('file', file);

      return unwrap(
        await client.POST('/api/events/{eventId}/photos', {
          params: { path: { eventId } },
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
        'Could not upload that photo',
      );
    },
    onSuccess: async () => invalidate(),
  });
}

/** Removes an event photo. */
export function useDeleteEventPhoto(
  eventId: string,
): UseMutationResult<void, ApiError, string> {
  const client = useApiClient();
  const invalidate = useInvalidateEvents();

  return useMutation<void, ApiError, string>({
    mutationFn: async (photoId) =>
      unwrapEmpty(
        await client.DELETE('/api/events/{eventId}/photos/{photoId}', {
          params: { path: { eventId, photoId } },
        }),
        'Could not remove that photo',
      ),
    onSuccess: async () => invalidate(),
  });
}
